/**
 * Node Renderer System
 *
 * Provides the interface for type-specific node rendering with LOD support.
 * Renderers decide what to render based on screen metrics - the canvas just
 * provides the metrics and layers.
 *
 * Key principles:
 * - Renderer owns LOD decisions based on ScreenMetrics
 * - Multiple representations: full → title → icon → dot
 * - Canvas doesn't know about LOD thresholds
 */

import type { UINode, ScreenMetrics, NodeType, WorldRect } from './types';
import type { HTMLLayer } from './html-layer';
import type { SVGLayer } from './svg-layer';
import type { WebGLLayer } from './webgl-layer';
import type { MeasureOptions } from './content-measure';

// Re-export ScreenMetrics for convenience
export type { ScreenMetrics } from './types';

/**
 * Render options that affect rendering behavior.
 */
export interface RenderOptions {
	/** Whether to simplify LOD when zooming out (default: true). When false, always use 'full' LOD. */
	simplifyLOD: boolean;
	/** Force texture rendering at distance without changing LOD content */
	forceTextureAtDistance: boolean;
	/** Threshold below which nodes render as texture (in screen pixels) */
	textureThreshold: number;
	/** Threshold for full LOD (in screen pixels) */
	lodFullThreshold: number;
	/** Threshold for title LOD (in screen pixels) */
	lodTitleThreshold: number;
	/** Threshold for icon LOD (in screen pixels) */
	lodIconThreshold: number;
	/** Enable full LOD level (default: true) */
	enableLodFull?: boolean;
	/** Enable title LOD level (default: true) */
	enableLodTitle?: boolean;
	/** Enable icon LOD level (default: true) */
	enableLodIcon?: boolean;
	/** Enable dot LOD level (default: true) */
	enableLodDot?: boolean;
	/** Force a specific LOD level (overrides all threshold calculations) */
	forceLod?: 'full' | 'title' | 'icon' | 'dot';
	/** Force texture rendering (WebGL) instead of HTML for all nodes */
	forceTextureRendering?: boolean;
}

/**
 * Render layers available to node renderers.
 */
export interface RenderLayers {
	html: HTMLLayerAccess;
	svg: SVGLayerAccess;
	/** WebGL layer for texture-based rendering (Phase 0.7+) */
	webgl: WebGLLayerAccess | null;
	/** Rendering options */
	options: RenderOptions;
}

/**
 * HTML layer access for renderers.
 * Provides methods to set node content.
 */
export interface HTMLLayerAccess {
	/** Get the container element for a node */
	getContainer(nodeId: string): HTMLElement | null;

	/** Set the content of a node (replaces existing) */
	setContent(nodeId: string, content: string | HTMLElement): void;

	/** Add a CSS class to a node */
	addClass(nodeId: string, className: string): void;

	/** Remove a CSS class from a node */
	removeClass(nodeId: string, className: string): void;

	/** Set a data attribute on a node */
	setDataAttribute(nodeId: string, key: string, value: string): void;

	/** Check if a node element was just created (needs forced content update) */
	isNewlyCreated(nodeId: string): boolean;
}

/**
 * SVG layer access for renderers.
 * For renderers that need to add SVG decorations.
 */
export interface SVGLayerAccess {
	/** Add an SVG element associated with a node */
	addElement(nodeId: string, element: SVGElement): void;

	/** Remove SVG elements for a node */
	removeElement(nodeId: string): void;
}

/**
 * WebGL layer access for renderers (Phase 0.7+).
 * Provides texture caching for efficient rendering of distant nodes.
 */
export interface WebGLLayerAccess {
	/** Check if a cached texture exists for a node */
	hasTexture(nodeId: string): boolean;

	/** Check if a cached texture exists and matches the content hash */
	hasValidTexture(nodeId: string, contentHash: string): boolean;

	/**
	 * Request texture generation for a node.
	 * The texture will be generated asynchronously using SnapDOM.
	 *
	 * @param nodeId - The node ID (compound: "actualNodeId:lodLevel")
	 * @param getElement - Callback to get the HTML element to capture
	 * @param contentHash - Hash of content for cache invalidation
	 * @param priority - Higher priority = generated sooner
	 * @param expectedLOD - Expected LOD level at capture time (for race condition validation)
	 * @param targetSize - Target max dimension in pixels (default: textureThreshold)
	 */
	requestTexture(
		nodeId: string,
		getElement: () => HTMLElement | null,
		contentHash: string,
		priority?: number,
		expectedLOD?: string,
		targetSize?: number
	): void;

	/**
	 * Draw a cached texture for a node.
	 * Returns true if the texture was drawn, false if not available.
	 *
	 * @param nodeId - The node ID
	 * @param worldRect - World coordinates for the quad
	 * @param opacity - Opacity (0-1)
	 * @param rotation - Rotation in radians (default: 0)
	 */
	drawCachedTexture(nodeId: string, worldRect: WorldRect, opacity?: number, rotation?: number): boolean;

	/** Invalidate a cached texture (e.g., when content changes) */
	invalidateTexture(nodeId: string): void;
}

/**
 * Level of Detail indicator.
 * Renderers report which LOD they're using for debugging/metrics.
 */
export type LODLevel = 'full' | 'title' | 'icon' | 'dot';

/**
 * Result of a render operation.
 * Contains metadata about what was rendered.
 */
export interface RenderResult {
	/** The LOD level used */
	lod: LODLevel;

	/** Whether the content changed (for texture caching) */
	changed: boolean;
}

/**
 * Node renderer interface.
 *
 * Renderers are responsible for:
 * 1. Deciding what LOD to render based on screen metrics
 * 2. Rendering the appropriate representation to the layers
 * 3. Reporting what LOD was used
 *
 * The renderer owns all LOD logic. The canvas just calls render()
 * with the current metrics and layers.
 */
export interface NodeRenderer {
	/**
	 * Render a node with the appropriate LOD.
	 *
	 * Called every frame for visible nodes. The renderer should:
	 * 1. Check screenMetrics to decide LOD
	 * 2. Render to appropriate layer(s)
	 * 3. Return info about what was rendered
	 *
	 * @param node - The node to render
	 * @param metrics - Screen size information for LOD decisions
	 * @param layers - Available rendering layers
	 * @returns Render result with LOD and change info
	 */
	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult;

	/**
	 * Get the preferred size for a node.
	 * Used for initial layout before rendering.
	 */
	getPreferredSize(node: UINode): { width: number; height: number };

	/**
	 * Get the content HTML for measurement (without side effects).
	 * Returns the same HTML that render() would produce for the 'full' LOD.
	 * Used by ContentMeasurer for auto-sizing.
	 */
	getContentHTML?(node: UINode): string;

	/**
	 * Get measurement constraints for this node type.
	 * Returns type-specific sizing hints (max readable width, min dimensions).
	 */
	getMeasureOptions?(node: UINode): MeasureOptions;

	/**
	 * Clean up any resources for a node.
	 * Called when a node is removed from the canvas.
	 */
	cleanup?(nodeId: string): void;
}

/**
 * Registry of node renderers by type.
 */
class RendererRegistry {
	private renderers: Map<NodeType, NodeRenderer> = new Map();
	private defaultRenderer: NodeRenderer | null = null;

	/**
	 * Register a renderer for a node type.
	 */
	register(type: NodeType, renderer: NodeRenderer): void {
		this.renderers.set(type, renderer);
	}

	/**
	 * Set the default renderer for unknown types.
	 */
	setDefault(renderer: NodeRenderer): void {
		this.defaultRenderer = renderer;
	}

	/**
	 * Get the renderer for a node type.
	 */
	get(type: NodeType): NodeRenderer | null {
		return this.renderers.get(type) ?? this.defaultRenderer;
	}

	/**
	 * Check if a renderer exists for a type (including default fallback).
	 */
	has(type: NodeType): boolean {
		return this.renderers.has(type) || this.defaultRenderer !== null;
	}

	/**
	 * Check if a renderer was explicitly registered for a type (no default fallback).
	 */
	hasRegistered(type: NodeType): boolean {
		return this.renderers.has(type);
	}

	/**
	 * Get all registered types.
	 */
	getTypes(): NodeType[] {
		return Array.from(this.renderers.keys());
	}
}

/**
 * Global renderer registry instance.
 */
export const rendererRegistry = new RendererRegistry();

/**
 * Helper to create an HTMLLayerAccess from an HTMLLayer.
 */
export function createHTMLLayerAccess(layer: HTMLLayer): HTMLLayerAccess {
	return {
		getContainer(nodeId: string): HTMLElement | null {
			return layer.getNodeElement(nodeId);
		},

		setContent(nodeId: string, content: string | HTMLElement): void {
			const element = layer.getNodeElement(nodeId);
			if (!element) return;

			if (typeof content === 'string') {
				element.innerHTML = content;
			} else {
				element.innerHTML = '';
				element.appendChild(content);
			}
		},

		addClass(nodeId: string, className: string): void {
			const element = layer.getNodeElement(nodeId);
			if (element) {
				element.classList.add(className);
			}
		},

		removeClass(nodeId: string, className: string): void {
			const element = layer.getNodeElement(nodeId);
			if (element) {
				element.classList.remove(className);
			}
		},

		setDataAttribute(nodeId: string, key: string, value: string): void {
			const element = layer.getNodeElement(nodeId);
			if (element) {
				element.dataset[key] = value;
			}
		},

		isNewlyCreated(nodeId: string): boolean {
			return layer.isNodeNewlyCreated(nodeId);
		}
	};
}

/**
 * Helper to create an SVGLayerAccess from an SVGLayer.
 */
export function createSVGLayerAccess(layer: SVGLayer): SVGLayerAccess {
	const nodeElements: Map<string, SVGElement[]> = new Map();

	return {
		addElement(nodeId: string, element: SVGElement): void {
			// Track elements per node for cleanup
			if (!nodeElements.has(nodeId)) {
				nodeElements.set(nodeId, []);
			}
			nodeElements.get(nodeId)!.push(element);
			// Note: actual SVG insertion would go through SVGLayer
		},

		removeElement(nodeId: string): void {
			const elements = nodeElements.get(nodeId);
			if (elements) {
				for (const el of elements) {
					el.remove();
				}
				nodeElements.delete(nodeId);
			}
		}
	};
}

/**
 * Helper to create a WebGLLayerAccess from a WebGLLayer (Phase 0.7+).
 * Returns null if the layer is null or unavailable.
 */
export function createWebGLLayerAccess(layer: WebGLLayer | null): WebGLLayerAccess | null {
	if (!layer || !layer.available) {
		return null;
	}

	return {
		hasTexture(nodeId: string): boolean {
			return layer.hasTexture(nodeId);
		},

		hasValidTexture(nodeId: string, contentHash: string): boolean {
			return layer.hasValidTexture(nodeId, contentHash);
		},

		requestTexture(
			nodeId: string,
			getElement: () => HTMLElement | null,
			contentHash: string,
			priority: number = 0,
			expectedLOD?: string,
			targetSize?: number
		): void {
			layer.requestTexture(nodeId, getElement, contentHash, priority, expectedLOD, targetSize);
		},

		drawCachedTexture(nodeId: string, worldRect: WorldRect, opacity: number = 1, rotation: number = 0): boolean {
			return layer.drawCachedTexture(nodeId, worldRect, opacity, rotation);
		},

		invalidateTexture(nodeId: string): void {
			layer.invalidateTexture(nodeId);
		}
	};
}

/**
 * LOD enable flags interface for calculateLOD.
 */
export interface LODEnableFlags {
	enableLodFull?: boolean;
	enableLodTitle?: boolean;
	enableLodIcon?: boolean;
	enableLodDot?: boolean;
	/** Force a specific LOD level, bypassing all threshold calculations */
	forceLod?: LODLevel;
}

/**
 * Calculate LOD level based on screen metrics and enabled LOD levels.
 * This is a helper for renderers - they can use their own logic.
 *
 * When a LOD level is disabled, the next enabled level is used instead.
 * For example, if 'title' is disabled, nodes that would show 'title' will show 'full' instead.
 *
 * @param metrics - Screen metrics for the node
 * @param options - Optional LOD enable flags (all enabled by default)
 */
export function calculateLOD(metrics: ScreenMetrics, options?: LODEnableFlags): LODLevel {
	// If forceLod is specified, return it directly (bypass all calculations)
	if (options?.forceLod) {
		return options.forceLod;
	}

	const screenSize = Math.max(metrics.width, metrics.height);

	// Get enabled states (default to true)
	const enableFull = options?.enableLodFull ?? true;
	const enableTitle = options?.enableLodTitle ?? true;
	const enableIcon = options?.enableLodIcon ?? true;
	const enableDot = options?.enableLodDot ?? true;

	// Calculate the "natural" LOD based on screen size
	let naturalLod: LODLevel;
	if (screenSize >= 150) naturalLod = 'full';
	else if (screenSize >= 60) naturalLod = 'title';
	else if (screenSize >= 20) naturalLod = 'icon';
	else naturalLod = 'dot';

	// If the natural LOD is enabled, use it
	if (naturalLod === 'full' && enableFull) return 'full';
	if (naturalLod === 'title' && enableTitle) return 'title';
	if (naturalLod === 'icon' && enableIcon) return 'icon';
	if (naturalLod === 'dot' && enableDot) return 'dot';

	// Natural LOD is disabled - find the closest enabled level
	// Strategy: try to find the next "higher detail" enabled level first,
	// then fall back to lower detail levels
	const lodOrder: LODLevel[] = ['full', 'title', 'icon', 'dot'];
	const enabledMap: Record<LODLevel, boolean> = {
		full: enableFull,
		title: enableTitle,
		icon: enableIcon,
		dot: enableDot
	};

	const naturalIndex = lodOrder.indexOf(naturalLod);

	// First, look for enabled levels with MORE detail (lower index)
	for (let i = naturalIndex - 1; i >= 0; i--) {
		if (enabledMap[lodOrder[i]]) {
			return lodOrder[i];
		}
	}

	// Then, look for enabled levels with LESS detail (higher index)
	for (let i = naturalIndex + 1; i < lodOrder.length; i++) {
		if (enabledMap[lodOrder[i]]) {
			return lodOrder[i];
		}
	}

	// No levels enabled - default to 'full' as fallback
	return 'full';
}
